import type { Schema } from "amplify/data/resource";

// ── Model aliases ─────────────────────────────────────────────────────────────

export type Transaction = Schema["Transaction"]["type"];
export type Payout = Schema["Payout"]["type"];
export type Invoice = Schema["Invoice"]["type"];
export type Receipt = Schema["Receipt"]["type"];

// ── List result ───────────────────────────────────────────────────────────────

export interface ListResult<T> {
  items: T[];
  nextToken: string | null;
}

// ── Date range ────────────────────────────────────────────────────────────────

export type DateRangePreset = "1h" | "24h" | "7d" | "30d" | "all" | "custom";

export interface CustomDateRange {
  from: number; // unix seconds
  to: number; // unix seconds
}

export type DateRangeFilter =
  | { preset: Exclude<DateRangePreset, "custom"> }
  | { preset: "custom"; range: CustomDateRange };

/**
 * Returns the start-of-range unix timestamp (seconds) for a preset.
 * Returns 0 for "all" (no lower bound — callers should skip the date filter).
 */
export function presetToFromTimestamp(
  preset: Exclude<DateRangePreset, "custom">,
): number {
  const now = Math.floor(Date.now() / 1000);
  switch (preset) {
    case "1h":
      return now - 3600;
    case "24h":
      return now - 86400;
    case "7d":
      return now - 7 * 86400;
    case "30d":
      return now - 30 * 86400;
    case "all":
      return 0;
  }
}

// ── Transaction filters ───────────────────────────────────────────────────────

export type TxStatus =
  "Incomplete" | "Succeeded" | "Failed" | "Refunded" | "Voided";
export type RefundState = "any" | "refunded" | "not_refunded" | "partial_only";
export type DisputeState = "any" | "disputed" | "not_disputed";

export interface TxFilter {
  searchQuery: string; // client-side substring on sourceChargeId, cposPayId
  statuses: TxStatus[]; // empty = all
  /** App vs terminal, derived from `terminalId`. Empty = all. */
  platforms: ("app" | "terminal")[];
  refundState: RefundState;
  disputeState: DisputeState;
  dateRange: DateRangeFilter;
}

export function defaultTxFilter(): TxFilter {
  return {
    searchQuery: "",
    statuses: [],
    platforms: [],
    refundState: "any",
    disputeState: "any",
    dateRange: { preset: "all" },
  };
}

// ── Payout filters ────────────────────────────────────────────────────────────

export type PayoutStatus =
  "pending" | "in_transit" | "paid" | "failed" | "canceled";

export interface PayoutFilter {
  statuses: PayoutStatus[]; // empty = all
  dateRange: DateRangeFilter;
}

export function defaultPayoutFilter(): PayoutFilter {
  return {
    statuses: [],
    dateRange: { preset: "all" },
  };
}

// ── Invoice filters ───────────────────────────────────────────────────────────

export type InvoiceStatus = "Draft" | "Sent" | "Paid" | "Cancelled";

export interface InvoiceFilter {
  searchQuery: string; // server-side `contains` filter on description and id
  statuses: InvoiceStatus[]; // empty = all
  dateRange: DateRangeFilter;
}

export function defaultInvoiceFilter(): InvoiceFilter {
  return {
    searchQuery: "",
    statuses: [],
    dateRange: { preset: "all" },
  };
}

// ── Stripe URL builders ───────────────────────────────────────────────────────
// REMOVED: chargeUrl, paymentIntentUrl, disputeUrl, payoutUrl
// All Stripe link building is centralized in @/lib/stripe-links.
// Import directly from there.

// ── Terminal tenders ──────────────────────────────────────────────────────────

/** One tender of a terminal sale, as written into `Transaction.tenders`. */
export interface TenderLine {
  amountCents: number;
  transactionId?: string | null;
  fundingType?: string | null;
  status?: string | null;
  reversedAmountCents?: number | null;
}

/**
 * A row's `tenders` JSON as lines. Accepts the raw AWSJSON string or an
 * already-parsed array; returns an empty list for anything unreadable.
 */
export function parseTenders(raw: unknown): TenderLine[] {
  let value: unknown = raw;
  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is TenderLine =>
      typeof item === "object" &&
      item !== null &&
      Number.isInteger((item as { amountCents?: unknown }).amountCents),
  );
}

// ── Formatting helpers ────────────────────────────────────────────────────────

export function formatCurrency(
  cents: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (cents == null) return "—";
  const amount = (cents / 100).toFixed(2);
  const code = (currency ?? "usd").toUpperCase();
  return `${code} ${amount}`;
}

export function formatRelative(unix: number): string {
  const now = Date.now();
  const then = unix * 1000;
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

export function formatAbsolute(unix: number): string {
  return new Date(unix * 1000).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatShortDate(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
